import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, CheckCircle2, Loader2, Plus, Save, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Owner event authoring — one page handles both "create new event" and
// "edit existing event + manage tasks". The mobile backend restricts task
// mutations to events with status="upcoming", so the tasks section
// disappears once the event goes live (matches the mobile app behaviour).
//
// Routes:
//   /owner/events/new?campsite=<id>
//   /owner/events/:eventId
export default function OwnerEventEditor() {
  const { eventId } = useParams();
  const [sp] = useSearchParams();
  const preselectCampsite = sp.get("campsite") || "";
  const isNew = !eventId || eventId === "new";
  const { api } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [campsites, setCampsites] = useState([]);
  const [form, setForm] = useState({
    campsite_id: preselectCampsite,
    name: "",
    description: "",
    start_at: "",
    end_at: "",
  });
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [taskDraft, setTaskDraft] = useState("");
  const [taskBusy, setTaskBusy] = useState(false);
  const [error, setError] = useState(null);

  // Load: owner's campsites (for the create dropdown), and if editing,
  // the event doc + its tasks.
  const load = useCallback(async () => {
    setError(null);
    try {
      const [csRes, evtRes] = await Promise.all([
        isNew ? api.get("/campsites/mine") : Promise.resolve(null),
        isNew ? Promise.resolve(null) : api.get(`/events/${eventId}`),
      ]);
      if (csRes) setCampsites(csRes.data || []);
      if (evtRes) {
        const e = evtRes.data;
        setEvent(e);
        setForm({
          campsite_id: e.campsite_id,
          name: e.name || "",
          description: e.description || "",
          start_at: (e.start_at || "").slice(0, 16),
          end_at: (e.end_at || "").slice(0, 16),
        });
        // Load tasks separately; the /events/{id} endpoint returns computed
        // counts but not the task list on many builds.
        try {
          const t = await api.get(`/events/${eventId}/tasks`);
          setTasks(Array.isArray(t.data) ? t.data : (t.data?.items || []));
        } catch {
          setTasks(e.tasks || []);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load event.");
    }
  }, [api, eventId, isNew]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        start_at: new Date(form.start_at).toISOString(),
        end_at:   new Date(form.end_at).toISOString(),
      };
      if (isNew) {
        const { data } = await api.post("/events", { campsite_id: form.campsite_id, ...payload });
        toast.success("Event created.");
        navigate(`/owner/events/${data.event_id}`, { replace: true });
      } else {
        await api.patch(`/events/${eventId}`, payload);
        toast.success("Event saved.");
        load();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save event.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async () => {
    if (isNew) return;
    if (!window.confirm("Delete this event and all its tasks? This can't be undone.")) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Event deleted.");
      navigate("/owner/events", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not delete event.";
      toast.error(detail);
    }
  };

  // ---- Task CRUD -----------------------------------------------------------
  const addTask = async (e) => {
    e.preventDefault();
    if (taskBusy || !taskDraft.trim()) return;
    setTaskBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/tasks`, {
        question: taskDraft.trim(),
      });
      setTasks((t) => [...t, data]);
      setTaskDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add task.");
    } finally {
      setTaskBusy(false);
    }
  };
  const editTask = async (task, question) => {
    try {
      await api.patch(`/events/${eventId}/tasks/${task.task_id}`, { question });
      setTasks((all) => all.map((x) => x.task_id === task.task_id ? { ...x, question } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not edit task.");
    }
  };
  const deleteTask = async (task) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/events/${eventId}/tasks/${task.task_id}`);
      setTasks((all) => all.filter((x) => x.task_id !== task.task_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete task.");
    }
  };

  const status = event?.status;
  const isUpcoming = !event || status === "upcoming";

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-5" data-testid="event-editor-page">
      <div>
        <Link to="/owner/events" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="event-editor-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Events
        </Link>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }}>
            {isNew ? "Create event" : "Edit event"}
          </h1>
          {status && (
            <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
              {status}
            </span>
          )}
        </div>
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}

      {/* Event form */}
      <form onSubmit={submit} className="card-elev p-5 flex flex-col gap-4" data-testid="event-form">
        {isNew && (
          <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            Campsite
            <select
              required value={form.campsite_id}
              onChange={(e) => setForm({ ...form, campsite_id: e.target.value })}
              className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
              style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
              data-testid="event-campsite-select"
            >
              <option value="">Select a campsite…</option>
              {campsites.map((c) => <option key={c.campsite_id} value={c.campsite_id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Name
          <input type="text" required minLength={2} maxLength={80} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
                 style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                 data-testid="event-name" />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Description
          <textarea rows={3} maxLength={500} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm resize-none font-normal normal-case tracking-normal"
                    style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                    data-testid="event-description" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            Starts
            <input type="datetime-local" required value={form.start_at}
                   onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                   className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
                   style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                   data-testid="event-start" />
          </label>
          <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
            Ends
            <input type="datetime-local" required value={form.end_at} min={form.start_at}
                   onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                   className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm font-normal normal-case tracking-normal"
                   style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                   data-testid="event-end" />
          </label>
        </div>
        <div className="flex items-center justify-between mt-2">
          <button type="submit" disabled={saving} className="btn btn-green" data-testid="event-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {isNew ? "Create event" : "Save changes"}</>}
          </button>
          {!isNew && (
            <button type="button" onClick={removeEvent} className="btn btn-light" style={{ color: "#B45309" }}
                    data-testid="event-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </form>

      {/* Tasks — only after the event exists, and only mutable while upcoming */}
      {!isNew && (
        <section className="card-elev p-5 flex flex-col gap-4" data-testid="event-tasks">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Tasks</h2>
            <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>{tasks.length} / 20</div>
          </div>
          {!isUpcoming && (
            <div className="text-xs p-3 rounded-xl"
                 style={{ background: "color-mix(in srgb, var(--premium) 10%, transparent)", color: "var(--premium)" }}>
              Tasks are locked once the event starts.
            </div>
          )}
          {tasks.length > 0 && (
            <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }}>
              {tasks.map((t) => (
                <TaskRow key={t.task_id} t={t} readOnly={!isUpcoming}
                         onEdit={(q) => editTask(t, q)} onDelete={() => deleteTask(t)} />
              ))}
            </div>
          )}
          {isUpcoming && (
            <form onSubmit={addTask} className="flex gap-2" data-testid="event-add-task-form">
              <input type="text" required maxLength={200} value={taskDraft}
                     onChange={(e) => setTaskDraft(e.target.value)}
                     placeholder="New task question…"
                     className="flex-1 px-3 py-2.5 rounded-xl border text-sm"
                     style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                     data-testid="event-task-input" />
              <button type="submit" disabled={taskBusy || !taskDraft.trim()} className="btn btn-green" data-testid="event-add-task">
                {taskBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add</>}
              </button>
            </form>
          )}
        </section>
      )}
    </div>
  );
}

function TaskRow({ t, readOnly, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.question || "");
  const save = () => {
    const v = text.trim();
    if (!v || v === t.question) { setEditing(false); return; }
    onEdit(v);
    setEditing(false);
  };
  return (
    <div className="py-2.5 flex items-start gap-3" data-testid={`event-task-${t.task_id}`}>
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold shrink-0 mt-0.5"
           style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
        {(t.order ?? 0) + 1}
      </div>
      {!editing ? (
        <>
          <div className="flex-1 text-sm" style={{ color: "var(--on-surface)" }}>{t.question}</div>
          {!readOnly && (
            <div className="shrink-0 flex items-center gap-1">
              <button onClick={() => setEditing(true)} className="text-xs font-bold underline underline-offset-4"
                      style={{ color: "var(--brand)" }} data-testid={`event-task-edit-${t.task_id}`}>Edit</button>
              <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-black/5"
                      title="Delete" data-testid={`event-task-delete-${t.task_id}`}>
                <Trash2 className="w-3.5 h-3.5 opacity-60" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex gap-2">
          <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                 className="flex-1 px-2 py-1.5 rounded-lg border text-sm"
                 style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                 data-testid={`event-task-input-${t.task_id}`} />
          <button onClick={save} className="btn btn-green" data-testid={`event-task-save-${t.task_id}`}>
            <CheckCircle2 className="w-4 h-4" />
          </button>
          <button onClick={() => { setEditing(false); setText(t.question || ""); }} className="btn btn-light">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
